import { useEffect, useMemo, useRef, useState } from 'react';
import { Download, Loader2, RefreshCw, ScanLine, CalendarDays, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import api from '@/services/api';
import { RANGE_PRESETS, computeRange, csvDownload } from './_shared';

const INR = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const Tile = ({ label, value, sub, cls = 'text-gray-900', testId }) => (
  <Card className="bg-white border-gray-200"><CardContent className="p-4">
    <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${cls}`} data-testid={testId}>{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
  </CardContent></Card>
);

// scope='store' hides the store filter (backend clamps to the logged-in store anyway)
const ProductSalesReport = ({ scope = 'hq' }) => {
  const { toast } = useToast();
  const init = computeRange('month');
  const [preset, setPreset] = useState('month');
  const [dateFrom, setDateFrom] = useState(init.date_from);
  const [dateTo, setDateTo] = useState(init.date_to);
  const [storeId, setStoreId] = useState('all');
  const [stores, setStores] = useState([]);
  const [mode, setMode] = useState('sold');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);
  const [tab, setTab] = useState('products');

  useEffect(() => {
    if (scope !== 'hq') return;
    api.get('/stores').then(r => setStores(r.data?.stores || r.data || [])).catch(() => {});
  }, [scope]);

  useEffect(() => {
    if (preset !== 'custom') { const r = computeRange(preset); setDateFrom(r.date_from); setDateTo(r.date_to); }
  }, [preset]);

  useEffect(() => { const t = setTimeout(() => setQ(qInput.trim()), 350); return () => clearTimeout(t); }, [qInput]);

  const load = async () => {
    const myReq = ++reqId.current;
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo, mode, page_size: 500 };
      if (q) params.q = q;
      if (scope === 'hq' && storeId !== 'all') params.store_id = storeId;
      const res = await api.get('/product-reports/product-sales', { params });
      if (myReq !== reqId.current) return; // a newer request has superseded this one
      setData(res.data);
    } catch (err) {
      toast({ title: 'Load failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { if (myReq === reqId.current) setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [dateFrom, dateTo, storeId, mode, q]);

  const rows = data?.rows || [];
  const daily = data?.daily || [];
  const totals = data?.totals || {};
  const csvRows = useMemo(() => rows.map(r => ({ Product: r.name, SKU: r.sku, Barcode: r.barcode || '', MRP: r.mrp ?? '', 'Units Sold': r.units_sold, Returned: r.units_returned, 'Net Units': r.net_units, Bills: r.bills, Discount: r.discount, Amount: r.amount })), [rows]);

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="product-sales-report">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Package className="w-6 h-6 text-teal-600" /> Product-wise Sales</h1>
          <p className="text-gray-500 text-sm">Units and amount sold per product for the selected period, with a date-wise summary</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="product-sales-refresh"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
          <Button variant="outline" size="sm" onClick={() => csvDownload(tab === 'daily' ? daily.map(d => ({ Date: d.date, Bills: d.bills, Units: d.units, Amount: d.amount })) : csvRows, `product_sales_${dateFrom}_${dateTo}.csv`)} data-testid="product-sales-csv"><Download className="w-4 h-4 mr-1" /> CSV</Button>
        </div>
      </div>

      <Card className="bg-white border-gray-200 mb-4"><CardContent className="p-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[9rem]"><label className="block text-xs text-gray-500 mb-1">Range</label>
          <Select value={preset} onValueChange={setPreset}><SelectTrigger data-testid="ps-range"><SelectValue /></SelectTrigger>
            <SelectContent>{RANGE_PRESETS.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent></Select></div>
        <div><label className="block text-xs text-gray-500 mb-1">From</label><Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPreset('custom'); }} className="w-36" data-testid="ps-from" /></div>
        <div><label className="block text-xs text-gray-500 mb-1">To</label><Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPreset('custom'); }} className="w-36" data-testid="ps-to" /></div>
        {scope === 'hq' && (
          <div className="min-w-[12rem]"><label className="block text-xs text-gray-500 mb-1">Store</label>
            <Select value={storeId} onValueChange={setStoreId}><SelectTrigger data-testid="ps-store"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All stores</SelectItem>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.code} — {s.name}</SelectItem>)}</SelectContent></Select></div>
        )}
        <div className="flex-1 min-w-[16rem]"><label className="block text-xs text-gray-500 mb-1">Product (scan barcode / type name / SKU)</label>
          <div className="relative"><ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Scan or type…" className="pl-10" data-testid="ps-search" /></div></div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 text-sm">
          {[['sold', 'Sold products'], ['all', 'All products']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setMode(k)} className={`px-3 py-1.5 rounded-md ${mode === k ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-500'}`} data-testid={`ps-mode-${k}`}>{l}</button>
          ))}
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Tile label="Products sold" value={totals.products_sold ?? 0} testId="ps-total-products" />
        <Tile label="Units (net of returns)" value={(totals.units ?? 0).toLocaleString('en-IN')} testId="ps-total-units" />
        <Tile label="Bills" value={totals.bills ?? 0} />
        <Tile label="Sales amount" value={INR(totals.amount)} cls="text-teal-700" testId="ps-total-amount" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-gray-200 mb-3">
          <TabsTrigger value="products" data-testid="ps-tab-products"><Package className="w-4 h-4 mr-1" /> Products ({data?.total_rows ?? 0})</TabsTrigger>
          <TabsTrigger value="daily" data-testid="ps-tab-daily"><CalendarDays className="w-4 h-4 mr-1" /> Date-wise summary ({daily.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="products">
          <Card className="bg-white border-gray-200"><CardContent className="p-0">
            {loading && !data ? <div className="py-12 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div> : rows.length === 0 ? (
              <div className="py-12 text-center text-gray-500">No products {mode === 'sold' ? 'sold' : 'found'} for this selection</div>
            ) : (
              <Table>
                <TableHeader><TableRow className="bg-gray-50">
                  <TableHead>Product</TableHead><TableHead>SKU / Barcode</TableHead><TableHead className="text-right">MRP</TableHead>
                  <TableHead className="text-right">Units sold</TableHead><TableHead className="text-right">Returned</TableHead><TableHead className="text-right">Net units</TableHead>
                  <TableHead className="text-right">Bills</TableHead><TableHead className="text-right">Discount</TableHead><TableHead className="text-right">Amount</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.product_id} className={`hover:bg-gray-50 ${r.net_units === 0 ? 'text-gray-400' : ''}`} data-testid={`ps-row-${r.sku}`}>
                      <TableCell className="font-medium text-gray-800">{r.name}</TableCell>
                      <TableCell className="text-xs text-gray-500 font-mono">{r.sku}{r.barcode && r.barcode !== r.sku ? ` · ${r.barcode}` : ''}</TableCell>
                      <TableCell className="text-right">{r.mrp != null ? INR(r.mrp) : '—'}</TableCell>
                      <TableCell className="text-right">{r.units_sold}</TableCell>
                      <TableCell className="text-right text-red-600">{r.units_returned || ''}</TableCell>
                      <TableCell className="text-right font-semibold">{r.net_units}</TableCell>
                      <TableCell className="text-right">{r.bills}</TableCell>
                      <TableCell className="text-right text-amber-700">{r.discount ? INR(r.discount) : ''}</TableCell>
                      <TableCell className="text-right font-semibold text-teal-700">{INR(r.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="daily">
          <Card className="bg-white border-gray-200"><CardContent className="p-0">
            {daily.length === 0 ? <div className="py-12 text-center text-gray-500">No sales in this period</div> : (
              <Table>
                <TableHeader><TableRow className="bg-gray-50"><TableHead>Date</TableHead><TableHead className="text-right">Bills</TableHead><TableHead className="text-right">Units</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                <TableBody>
                  {daily.map(d => (
                    <TableRow key={d.date} data-testid={`ps-day-${d.date}`}><TableCell className="font-medium">{fmtDate(d.date)}</TableCell><TableCell className="text-right">{d.bills}</TableCell><TableCell className="text-right">{d.units}</TableCell><TableCell className="text-right font-semibold text-teal-700">{INR(d.amount)}</TableCell></TableRow>
                  ))}
                  <TableRow className="bg-gray-50 font-bold"><TableCell>Total</TableCell><TableCell className="text-right">{totals.bills}</TableCell><TableCell className="text-right">{totals.units}</TableCell><TableCell className="text-right text-teal-700">{INR(totals.amount)}</TableCell></TableRow>
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProductSalesReport;
